<template>
  <nav class="hms-topbar">
    <div class="container">
      <a href="javascript:void(0)" @click="$router.push('/')" class="hms-brand">
        <img :src="$brand.logo" alt="HMS" class="hms-brand-logo" />
        <span class="hms-brand-text d-none d-sm-inline">HMS</span>
      </a>

      <span class="hms-inst-name" v-if="hospitalName">{{ hospitalName }}</span>

      <div class="ms-auto d-flex align-items-center gap-2">
        <button class="hms-icon-btn" @click="toggleTheme" :title="darkMode?'Light mode':'Dark mode'">
          <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'"></i>
        </button>

        <!-- Notification bell — access requests (Platform Admins) + per-user in-app notifications. -->
        <div class="hms-bell-wrap" style="position:relative;">
          <button class="hms-icon-btn" @click.stop="toggleBell" title="Notifications">
            <i class="mdi mdi-bell-outline"></i>
            <span v-if="totalBellCount > 0" class="hms-bell-badge">{{ totalBellCount > 9 ? '9+' : totalBellCount }}</span>
          </button>
          <div v-if="showBell" class="hms-bell-drop" @click.stop>
            <div class="hms-bell-header">
              <strong>Notifications</strong>
              <a v-if="myNotifications.length" href="javascript:void(0)" @click="markAllRead" class="hms-bell-viewall">Mark all read</a>
            </div>

            <div v-if="totalBellCount === 0" class="hms-bell-empty">
              <i class="mdi mdi-check-decagram text-success"></i> You're all caught up.
            </div>

            <div v-else>
              <!-- Access requests (Platform Admin) -->
              <div v-if="isPlatformAdmin && accessCount > 0" class="hms-bell-item" @click="goToAccessRequests">
                <div class="hms-bell-item-icon"><i class="mdi mdi-shield-key-outline"></i></div>
                <div class="flex-grow-1">
                  <div class="hms-bell-item-title">
                    <strong>{{ accessCount }}</strong> pending access request<span v-if="accessCount !== 1">s</span>
                  </div>
                  <div class="hms-bell-item-sub">Tap to review and approve or reject.</div>
                </div>
                <i class="mdi mdi-chevron-right"></i>
              </div>

              <!-- Per-user notifications (lab results ready, etc.) -->
              <div v-for="n in myNotifications" :key="n.notification_id" class="hms-bell-item"
                   :class="{ 'hms-bell-unread': !n.is_read }" @click="openNotification(n)">
                <div class="hms-bell-item-icon">
                  <i class="mdi" :class="notifIcon(n.type)"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="hms-bell-item-title">{{ n.title }}</div>
                  <div class="hms-bell-item-sub">{{ n.message }}</div>
                  <div class="hms-bell-item-time hms-muted-sm">{{ formatRelTime(n.created_at) }}</div>
                </div>
                <i class="mdi mdi-chevron-right"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="hms-nav-user" @click="showDrop=!showDrop">
          <div class="hms-nav-avatar">{{ initials }}</div>
          <span class="d-none d-md-inline hms-nav-username">{{ displayName || userName }}</span>
          <i class="mdi mdi-chevron-down hms-nav-caret"></i>
        </div>

        <div v-if="showDrop" class="hms-nav-drop" @click.stop>
          <div class="hms-nav-drop-header">
            <div class="hms-nav-drop-name">{{ displayName || userName }}</div>
            <div class="hms-nav-drop-role">{{ prettyRole }}</div>
          </div>

          <a href="javascript:void(0)" @click="go('/dashboard')" class="hms-nav-drop-item">
            <i class="mdi mdi-view-dashboard me-2"></i>{{ t('nav.dashboard','Dashboard') }}
          </a>
          <a v-if="isPlatformAdmin" href="javascript:void(0)" @click="go('/platform')" class="hms-nav-drop-item hms-nav-drop-admin">
            <i class="mdi mdi-shield-crown me-2"></i>Platform Console
          </a>
          <a v-if="canAccess('admin')" href="javascript:void(0)" @click="go('/admin')" class="hms-nav-drop-item hms-nav-drop-admin">
            <i class="mdi mdi-shield-crown me-2"></i>Admin
          </a>
          <a href="javascript:void(0)" @click="go('/profile')" class="hms-nav-drop-item">
            <i class="mdi mdi-account-cog me-2"></i>{{ t('nav.profile','My Profile') }}
          </a>
          <a href="javascript:void(0)" @click="go('/change-password')" class="hms-nav-drop-item">
            <i class="mdi mdi-key-variant me-2"></i>{{ t('nav.change_password','Change Password') }}
          </a>
          <a href="javascript:void(0)" @click="go('/settings')" class="hms-nav-drop-item">
            <i class="mdi mdi-cog me-2"></i>{{ t('nav.settings','Settings') }}
          </a>

          <div class="hms-nav-drop-divider"></div>

          <div class="hms-nav-drop-item" style="cursor:default;">
            <i class="mdi mdi-translate me-2"></i>
            <span class="me-2">{{ t('nav.language','Language') }}</span>
            <select v-model="i18n.state.lang" @change="onLangChange($event.target.value)" class="hms-nav-lang-select">
              <option v-for="L in i18n.state.available" :key="L.code" :value="L.code">{{ L.label }}</option>
            </select>
          </div>

          <div class="hms-nav-drop-divider"></div>

          <a href="javascript:void(0)" @click="logout" class="hms-nav-drop-item hms-nav-drop-danger">
            <i class="mdi mdi-logout me-2"></i>{{ t('nav.sign_out','Sign Out') }}
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "Navigation",
  data() {
    return {
      showDrop:     false,
      showBell:     false,
      accessCount:  0,
      myNotifications: [],
      myNotifCount: 0,
      pollTimer:    null,
      darkMode:     (localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light") !== "light",
      userName:     "",
      displayName:  "",
      initials:     "",
      userRole:     "",
      hospitalName: "",
      hospitalId:   "",
      i18n:         useI18n()
    };
  },
  computed: {
    prettyRole() {
      if (!this.userRole) return "";
      return this.userRole.split('_').map(function(s){
        return s.charAt(0).toUpperCase() + s.slice(1);
      }).join(' ');
    },
    isPlatformAdmin() {
      return this.userRole === 'platform_admin';
    },
    totalBellCount() {
      return (this.isPlatformAdmin ? this.accessCount : 0) + this.myNotifCount;
    }
  },
  mounted() {
    this.userName     = sessionStorage.getItem("userName")    || "";
    this.displayName  = sessionStorage.getItem("displayName") || "";
    this.userRole     = sessionStorage.getItem("userRole")    || "";
    this.hospitalName = sessionStorage.getItem("hospitalName")|| "";
    this.hospitalId   = sessionStorage.getItem("hospitalId")  || "";
    var name = this.displayName || this.userName;
    var parts = name.trim().split(/\s+/);
    this.initials = parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : (name.slice(0,2) || "?").toUpperCase();
    document.addEventListener("click", this.handleClickOutside);

    // Kick off notification polls — everyone gets in-app notifications,
    // Platform Admins also get the access-request counter.
    this.refreshAll();
    this.pollTimer = setInterval(this.refreshAll, 30000);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    async onLangChange(lang) {
      await this.i18n.setLang(lang);
    },
    handleClickOutside(e) {
      if (!e.target.closest('.hms-nav-user') && !e.target.closest('.hms-nav-drop')) {
        this.showDrop = false;
      }
      if (!e.target.closest('.hms-bell-wrap') && !e.target.closest('.hms-bell-drop')) {
        this.showBell = false;
      }
    },
    toggleBell() {
      this.showBell = !this.showBell;
      if (this.showBell) this.refreshAll();
    },
    async refreshAll() {
      await Promise.all([this.refreshAccessCount(), this.refreshMyNotifications()]);
    },
    async refreshAccessCount() {
      if (!this.isPlatformAdmin) return;
      try {
        const { data } = await executeJson({ function: "getAccessRequestCount" }, hms);
        if (data && data.success) this.accessCount = (data.data && data.data.count) || 0;
      } catch (_) { /* silent */ }
    },
    async refreshMyNotifications() {
      try {
        const { data } = await executeJson({ function: "getMyNotifications" }, hms);
        if (data && data.success) {
          const d = data.data || {};
          this.myNotifications = d.Recent || [];
          this.myNotifCount    = d.Count  || 0;
        }
      } catch (_) { /* silent */ }
    },
    async openNotification(n) {
      // Mark read first so the badge decrements immediately
      try {
        await executeJson({ function: "markNotificationRead", notification_id: n.notification_id }, hms);
        n.is_read = 1;
        this.myNotifCount = Math.max(0, this.myNotifCount - 1);
      } catch (_) {}
      this.showBell = false;
      var route = this.routeForNotification(n);
      if (route) this.$router.push(route).catch(function(){});
    },

    // Where should a click on this notification take the user?
    // Priority: explicit type → reference_type → patient chart fallback.
    routeForNotification(n) {
      var type    = (n.type || '').toLowerCase();
      var refType = (n.reference_type || '').toLowerCase();

      // Explicit type mapping (highest priority)
      var byType = {
        lab_result_ready:  n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/lab" },
        lab_result:        n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/lab" },
        radiology_ready:   n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/radiology" },
        radiology_result:  n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/radiology" },
        prescription:      n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/pharmacy" },
        appointment:       { path: "/appointments" },
        referral:          { path: "/referrals" },
        low_stock:         { path: "/pharmacy",  query: { tab: "stock" } },
        stock_alert:       { path: "/pharmacy",  query: { tab: "stock" } },
        license_expiry:    { path: "/hrh",        query: { tab: "licenses" } },
        licence_renewal:   { path: "/hrh",        query: { tab: "licenses" } },
        licence_expiry:    { path: "/hrh",        query: { tab: "licenses" } },
        monthly_report:    { path: "/reports" },
        report_ready:      { path: "/reports" },
        maternal_alert:    { path: "/maternal-safety" },
        access_request:    { path: "/platform",   query: { tab: "access" } },
        consent_request:   n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/dashboard" },
        payment_received:  { path: "/queue" }
      };
      if (byType[type]) return byType[type];

      // Reference-type mapping (for generic types like 'warning', 'alert', 'info')
      var byRef = {
        pharmacy:        { path: "/pharmacy" },
        pharmacy_stock:  { path: "/pharmacy",       query: { tab: "stock" } },
        stock:           { path: "/pharmacy",       query: { tab: "stock" } },
        staff_licenses:  { path: "/hrh",            query: { tab: "licenses" } },
        licenses:        { path: "/hrh",            query: { tab: "licenses" } },
        reports:         { path: "/reports" },
        lab:             n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/lab" },
        lab_request:     n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/lab" },
        radiology:       n.patient_id ? { path: "/patients/" + n.patient_id + "/chart" } : { path: "/radiology" },
        appointment:     { path: "/appointments" },
        referral:        { path: "/referrals" },
        admission:       { path: "/admissions" },
        maternity:       { path: "/maternity" },
        queue_ticket:    { path: "/queue" }
      };
      if (byRef[refType]) return byRef[refType];

      // Fallback: patient chart if we have a patient, otherwise dashboard
      if (n.patient_id) return { path: "/patients/" + n.patient_id + "/chart" };
      return { path: "/dashboard" };
    },
    async markAllRead() {
      try {
        await executeJson({ function: "markNotificationRead", notification_id: 0 }, hms);
        this.myNotifications.forEach(function(n) { n.is_read = 1; });
        this.myNotifCount = 0;
      } catch (_) {}
    },
    notifIcon(type) {
      switch ((type || '').toLowerCase()) {
        case 'payment_received':  return 'mdi-cash-check';
        case 'lab_result_ready':
        case 'lab_result':        return 'mdi-flask-outline';
        case 'radiology_ready':
        case 'radiology_result':  return 'mdi-radioactive';
        case 'appointment':       return 'mdi-calendar-clock';
        case 'referral':          return 'mdi-truck-fast-outline';
        case 'low_stock':
        case 'stock_alert':
        case 'warning':           return 'mdi-package-variant-closed-remove';
        case 'license_expiry':
        case 'licence_renewal':
        case 'licence_expiry':
        case 'alert':             return 'mdi-file-certificate-outline';
        case 'monthly_report':
        case 'report_ready':
        case 'info':              return 'mdi-file-chart-outline';
        case 'access_request':    return 'mdi-shield-key-outline';
        case 'consent_request':   return 'mdi-file-lock-outline';
        default:                  return 'mdi-bell-outline';
      }
    },
    formatRelTime(ts) {
      if (!ts) return '';
      const t = new Date(String(ts).replace(' ', 'T'));
      if (isNaN(t)) return String(ts);
      const diff = Math.floor((Date.now() - t.getTime()) / 1000);
      if (diff < 60)    return diff + 's ago';
      if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      return Math.floor(diff / 86400) + 'd ago';
    },
    goToAccessRequests() {
      this.showBell = false;
      this.$router.push({ path: "/platform", query: { tab: "access" } }).catch(function(){});
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      var theme = this.darkMode ? "dark" : "light";
      localStorage.setItem("hms_theme", theme);
      document.documentElement.setAttribute("data-theme", theme);
    },
    canAccess(menuKey) {
      // Admin link visible only to admin-tier roles
      if (menuKey === 'admin') {
        return ['super_admin', 'hospital_admin', 'branch_manager'].indexOf(this.userRole) >= 0;
      }
      return true;
    },
    go(path) {
      this.showDrop = false;
      this.$router.push(path).catch(function(){});
    },
    async logout() {
      try { await executeJson({ function: "logoutUser" }, hms); } catch(e) {}
      sessionStorage.clear();
      localStorage.removeItem("authToken");
      this.showDrop = false;
      this.$router.push("/login");
    }
  }
};
</script>

<style scoped>
.hms-topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  background: var(--hms-bg1);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--hms-br);
  padding: 8px 0;
}
.hms-topbar .container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hms-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}
.hms-brand-logo { height: 36px; }
.hms-brand-text {
  color: var(--hms-h);
  font-weight: 800;
  letter-spacing: 1px;
  font-size: 14px;
}

.hms-inst-name {
  color: var(--hms-m);
  font-size: 14px;
  font-weight: 500;
  padding-left: 12px;
  border-left: 1px solid var(--hms-br);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.hms-icon-btn {
  background: var(--hms-bg3);
  border: 1px solid var(--hms-br);
  color: var(--hms-primary);
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: all .15s;
}
.hms-icon-btn:hover {
  background: var(--hms-primary-lt);
  border-color: var(--hms-primary);
}

.hms-nav-user {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid var(--hms-br);
  position: relative;
  transition: border-color .15s;
}
.hms-nav-user:hover { border-color: var(--hms-primary); }
.hms-nav-avatar {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: var(--hms-primary-lt);
  color: var(--hms-primary);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
}
.hms-nav-username { color: var(--hms-h); font-size: 13px; font-weight: 600; }
.hms-nav-caret { color: var(--hms-s); }

.hms-nav-drop {
  position: absolute;
  top: 56px;
  right: 16px;
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 10px;
  min-width: 240px;
  z-index: 200;
  box-shadow: 0 8px 30px rgba(0,0,0,.18);
  overflow: hidden;
}
.hms-nav-drop-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--hms-br);
}
.hms-nav-drop-name { color: var(--hms-h); font-weight: 600; font-size: 14px; }
.hms-nav-drop-role { color: var(--hms-s); font-size: 12px; }
.hms-nav-drop-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  color: var(--hms-m);
  text-decoration: none;
  font-size: 13px;
  transition: all .15s;
}
.hms-nav-drop-item:hover {
  background: var(--hms-primary-lt);
  color: var(--hms-primary);
}
.hms-nav-drop-admin { color: var(--hms-primary); font-weight: 700; }
.hms-nav-drop-divider { border-top: 1px solid var(--hms-br); margin: 4px 0; }
.hms-nav-drop-danger { color: var(--hms-danger) !important; }

/* ── Bell / notifications ─────────────────────────── */
.hms-bell-badge {
  position: absolute;
  top: 2px; right: 2px;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 5px;
  border-radius: 999px;
  border: 2px solid var(--hms-bg1, #fff);
  min-width: 16px;
  text-align: center;
}
.hms-bell-drop {
  position: absolute;
  top: 46px; right: 0;
  width: 340px;
  background: var(--hms-bg1, #fff);
  border: 1px solid var(--hms-br, #e2e8f0);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.12);
  z-index: 1000;
  overflow: hidden;
}
.hms-bell-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--hms-br, #eef1f5);
  font-size: 13px;
  color: var(--hms-h);
}
.hms-bell-viewall {
  font-size: 12px;
  color: var(--hms-primary);
  text-decoration: none;
}
.hms-bell-viewall:hover { text-decoration: underline; }
.hms-bell-empty {
  padding: 20px 14px;
  text-align: center;
  color: var(--hms-s);
  font-size: 13px;
}
.hms-bell-item {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid var(--hms-br, #eef1f5);
  transition: background 0.15s ease;
}
.hms-bell-item:hover { background: var(--hms-bg3, #f8fafc); }
.hms-bell-item-icon {
  width: 34px; height: 34px; border-radius: 50%;
  background: rgba(245,130,31,0.12); color: #F5821F;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
}
.hms-bell-item-title { font-size: 13px; color: var(--hms-h); }
.hms-bell-item-sub   { font-size: 11.5px; color: var(--hms-s); margin-top: 2px; line-height: 1.4; }
.hms-bell-item-time  { font-size: 10px; color: var(--hms-m); margin-top: 3px; }
.hms-bell-item.hms-bell-unread { background: rgba(43,108,176,0.06); }
.hms-bell-item.hms-bell-unread .hms-bell-item-title { font-weight: 700; color: var(--hms-primary); }
</style>
